// Archivo para manejar la creación de consumidores desde los wizards y comprimir
//
// ConsumerServices.js
import api from './api'
import LZString from 'lz-string'
import { getLayoutsByProduct } from './layoutService'

/**
 * Obtener clientes (solo globalAdmin lo usará en el wizard/bulk).
 * Requiere que tu backend tenga GET /clients respetando roles.
 */
export const getClients = async () => {
  try {
    const response = await api.get('/clients')
    return response.data
  } catch (error) {
    console.error('[ConsumerService] Error al obtener clientes:', error)
    throw error
  }
}

/**
 * Obtener productos por cliente.
 * - Si se pasa clientId: usa /products/by-client/:clientId (para globalAdmin).
 * - Si NO se pasa clientId: usa /products (backend usa token).
 */
export const getProductsByClient = async (clientId) => {
  try {
    // Si no me pasas clientId -> el backend filtra por token
    if (clientId === undefined || clientId === null || clientId === '') {
      const response = await api.get('/products')
      return response.data
    }

    // Si me pasas clientId -> globalAdmin (o rol permitido) apunta a un cliente específico
    const response = await api.get(`/products/by-client/${clientId}`)
    return response.data
  } catch (error) {
    console.error('[ConsumerService] Error al obtener productos:', error)
    throw error
  }
}

/**
 * Obtener layouts aplicados a un producto
 * @param {string|number} productId
 */
export const getLayoutByProduct = async (productId) => {
  return getLayoutsByProduct(productId)
}

/**
 * Crear un consumidor individual
 * @param {Object} consumerData - incluye full_name, phone, email, custom_fields, product_id
 */
export const createConsumer = async (consumerData) => {
  try {
    const response = await api.post('/consumers/register', consumerData)
    return response.data
  } catch (error) {
    console.error('[ConsumerService] Error al crear consumidor:', error)
    throw error
  }
}

/**
 * Obtener consumidores por producto
 * @param {string|number} productId
 */
export const getConsumersByProduct = async (productId) => {
  const response = await api.get(`/products/${productId}/consumers`)
  return response.data
}

// Carga masiva de consumidores
export const uploadConsumerFile = async (formData, config = {}) => {
  try {
    const response = await api.post('/consumers/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
      ...config,
    })
    return response.data
  } catch (error) {
    console.error('[ConsumerService] Error en carga masiva:', error)
    throw error
  }
}

/**
 * Subir archivo comprimido para auditoría
 * @param {Object} payload - Debe incluir: file_name, product_id, original_size_kb, compressed_data, type
 */
export const uploadCompressedFile = async (payload) => {
  const response = await api.post('/uploads/audit', payload)
  return response.data
}

/**
 * Procesar y subir múltiples consumidores (uno por uno)
 * Nota: si tu backend requiere product_id por fila, este método debe recibir productId
 * y agregarlo al payload. Si usas /consumers/bulk-upload, probablemente NO necesitas
 * este método para el flujo masivo principal.
 */
export const bulkCreateConsumers = async (
  rows,
  layoutConfig,
  onProgress = () => {}
) => {
  let created = 0

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const payload = {
      full_name: row.full_name || 'Sin nombre',
      phone: row.phone || '',
      email: row.email || '',
      custom_fields: {},
    }

    layoutConfig.forEach((field) => {
      if (row[field.name]) {
        payload.custom_fields[field.name] = row[field.name]
      }
    })

    try {
      await createConsumer(payload)
      created++
    } catch (e) {
      console.warn(
        `❌ Error fila ${i + 1}:`,
        e?.response?.data?.error || e.message
      )
    }

    const progress = Math.round(((i + 1) / rows.length) * 100)
    onProgress(progress)
  }

  return created
}

/**
 * Comprimir archivo JSON para auditoría
 * @param {Object|Array} originalData
 * @returns {Object} { compressed, originalSizeKB }
 */
export const compressForAudit = (originalData) => {
  const original = JSON.stringify(originalData)
  const compressed = LZString.compressToUTF16(original)
  const sizeKB = new Blob([original]).size / 1024
  return { compressed, originalSizeKB: sizeKB.toFixed(2) }
}
